/**
 * @theme/bootstrap — single-entry module that registers every theme component.
 *
 * Loaded once from `theme.liquid` via:
 *   <script type="module">import '@theme/bootstrap';</script>
 *
 * Each import below has the side effect of running its `defineComponent('tag', Class)` registration.
 * Component code is itself lazy-friendly: the element doesn't do work until it's `connectedCallback`'d
 * by the parser.
 *
 * If you add a new component, import it here. Browsers cache modules per URL so adding one more import
 * is essentially free after the initial page load.
 */

/* Phase 1 / Wave 1a — base classes */
import '@theme/details-disclosure';     // <details-disclosure>, <header-menu>
import '@theme/details-modal';          // <details-modal>
import '@theme/modal-dialog';           // <modal-dialog>
import '@theme/modal-opener';           // <modal-opener>
import '@theme/deferred-media';         // <deferred-media>
import '@theme/menu-drawer';            // <menu-drawer>, <header-drawer>

/* Wave 1b — simple standalones */
import '@theme/copy-button';            // <copy-button>, <popup-copy-button>
import '@theme/page-scroll-progress';   // <page-scroll-progress>
import '@theme/countdown-timer';        // <countdown-timer>, <date-countdown-timer>
import '@theme/tnc-checkbox';           // <tnc-chekcbox> (typo preserved)
import '@theme/show-more';              // <show-more-button>
import '@theme/share';                  // <share-button>

/* Wave 2 — interactive UI */
import '@theme/parallax-hero';          // <parallax-hero>
import '@theme/hotspot-button';         // <hotspot-button>
import '@theme/content-tabs';           // <content-tabs>
import '@theme/contact-form';           // <contact-form>
import '@theme/localization-form';      // <localization-form>
import '@theme/products-mega-menu';     // <products-mega-menu>
import '@theme/product-recommendations';// <product-recommendations>
import '@theme/slider';                 // <slider-component>, <slideshow-component>
import '@theme/splide';                 // <splide-component>
import '@theme/insta-stories';          // <insta-stories>

/* Wave 3 — commerce critical path */
import '@theme/quantity-input';         // <quantity-input>
import '@theme/clickable-discount';     // <clickable-discount>
import '@theme/section-group';          // <section-group>
import '@theme/search-form';            // <search-form>
import '@theme/predictive-search';      // <predictive-search>
import '@theme/variant-selects';        // <variant-selects> (+3 subclass tags)
import '@theme/product-form';           // <product-form>
import '@theme/sticky-atc';             // <sticky-atc>
import '@theme/cart';                   // <cart-drawer>, <cart-items>, <cart-drawer-items>, <cart-note>, <cart-remove-button>

/* Wave 4 — commerce extras */
import '@theme/dynamic-dates';          // <dynamic-dates>
import '@theme/internal-video';         // <internal-video>
import '@theme/comparison-slider';      // <comparison-slider>
import '@theme/promo-popup';            // <promo-popup>
import '@theme/custom-product-field';   // <custom-product-field>
import '@theme/product-info';           // <product-info>, <product-info-upsell>
import '@theme/quantity-breaks';        // <quantity-breaks>
import '@theme/quantity-gifts';         // <quantity-gifts>, <quantity-break-upsells>
import '@theme/cart-upsell';            // <cart-drawer-upsell>, <cart-drawer-gift>
import '@theme/bundle-deals';           // <bundle-deals>, <main-bundle-offer>
import '@theme/subscription-widget';    // <shrine-subscription-widget>

/* Wave 5 — modular component rewrites */
import '@theme/cart-notification';      // <cart-notification>
import '@theme/customer';               // (DOMContentLoaded helpers)
import '@theme/facets';                 // <facet-filters-form>, <price-range>, <facet-remove>
import '@theme/magnify';                // (side-effect: enableZoomOnHover)
import '@theme/main-search';            // <main-search>
import '@theme/media-gallery';          // <media-gallery>
import '@theme/password-modal';         // <password-modal>
import '@theme/pickup-availability';    // <pickup-availability>, <pickup-availability-drawer>
import '@theme/product-modal';          // <product-modal>
import '@theme/product-model';          // <product-model>
import '@theme/quick-add';              // <quick-add-modal>
import '@theme/theme-editor';           // (Shopify design-mode helpers)

/* Make sure key utilities are reachable globally for any inline scripts that still expect them. */
import { pauseAllMedia } from '@theme/utilities';
import { trapFocus, releaseFocusTrap } from '@theme/focus';
/** @type {any} */ const w = window;
if (!w.pauseAllMedia) w.pauseAllMedia = pauseAllMedia;
if (!w.trapFocus) w.trapFocus = trapFocus;
if (!w.removeTrapFocus) w.removeTrapFocus = releaseFocusTrap;

/* Replace the no-js class on the html element. */
document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');
